import type {Contract, ContractHistory, Employee} from "$lib/types/employee";
import dayjs from "dayjs";
import {asValue} from "$lib/utils/lenkradDate";
import {formatCurrency} from "$lib/utils/currency";
import {contractTypes} from "$lib/utils/templating/contractTypes";
import ImageRenderer from "$lib/components/ui/ImageRenderer.svelte";
import DocumentPreview from "$lib/components/DocumentPreview.svelte";



export const elseHeader = [
    {
        label: 'Datum',
        sortable: true,
        movable: true,
        property: 'createdAt',
        render: (row: ContractHistory) => dayjs(asValue(row.createdAt)).format('DD.MM.YYYY')
    },
    {
        label: 'Dokument',
        sortable: true,
        movable: true,
        property: 'contractDocument',
        render: (row: ContractHistory) => row.contractDocument?.location ? '<a href="' + row.contractDocument.location + '" target="_blank" rel="noreferrer noopener">'+ row.contractDocument.name+'</a>' : 'Kein Dokument'
    },
    {
        label: 'Ende der Tätigkeit',
        movable: true,
        property: 'endDate',
        sortable: true,
        render: (row: ContractHistory) => dayjs(asValue(row.endDate)).format('DD.MM.YYYY')
    },
    {
        label: 'Berifft',
        movable: true,
        property: 'contractDocument',
        render: (row: ContractHistory) => row.hiredAs
    }
]

export const ptoHeader = [
    {
        label: 'Von',
        sortable: true,
        movable: true,
        property: 'startDate',
        class: 'w-28',
        render: (row: ContractHistory) => colored(row, dayjs(asValue(row.startDate)).format('DD.MM.YYYY'))
    },
    {
        label: 'Bis',
        sortable: true,
        movable: true,
        property: 'startDate',
        render: (row: ContractHistory) => colored(row, whenTerminatedEnd(row))
    },
    {
        label: 'Urlaubstage',
        sortable: true,
        movable: true,
        property: 'ptoDays',
    },
    {
        label: 'HistoryID',
        movable: true,
        sortable: true,
        property: 'id',
    }
]



export const contractHeader = (contracts:Contract[], employee:Employee, costCenters: any[]) => ([
    {
        label: 'Von',
        sortable: true,
        movable: true,
        property: 'startDate',
        class: 'w-28',
        render: (row: ContractHistory) => colored(row, dayjs(asValue(row.startDate)).format('DD.MM.YYYY'))
    },
    {
        label: 'Bis',
        sortable: true,
        movable: true,
        property: 'startDate',
        render: (row: ContractHistory) => colored(row, whenTerminatedEnd(row))
    },
    {
        label: 'Gruppe',
        sortable: true,
        movable: true,
        property: 'group',
        render: (row: ContractHistory) => colored(row, row.group)
    },{
        label: 'Status',
        movable: true,
        property: 'id',
        render: (row: ContractHistory) => colored(row, employee.status)
    },
    {
        label: 'Vertragstyp',
        sortable: true,
        movable: true,
        property: 'contractType',
        render: (row: ContractHistory) => colored(row, contractTypes.find(y => y.value === contracts.find(x => x.id === row.contractId)?.contractType)?.name || 'Unbekannt')
    },

    {
        label: 'Stunden',
        sortable: true,
        movable: true,
        property: 'workDaysPerWeek',
        render: (row: ContractHistory) => colored(row, (row.workDaysPerWeek * row.workHoursPerDay * 4.3).toFixed(0) + ' h')
    },
    {
        label: 'Typ',
        movable: true,
        property: 'historyType',
        render: (row: ContractHistory) => colored(row, (contractTypes.find(x => x.value === row.historyType)?.name || 'Arbeitsvertrag'))
    },
    {
        label: 'Kostenstelle',
        movable: true,
        property: 'costCenterId',
        render: (row: ContractHistory) => colored(row, costCenters.find(x => x.id === row.costCenterId)?.name)
    },
    {
        label: 'Dokument',
        sortable: false,
        movable: true,
        property: 'contractDocument',
        component: DocumentPreview

        // render: (row: ContractHistory) => row.contractDocument?.location ? '<a href="' + row.contractDocument.location + '" target="_blank" rel="noreferrer noopener">Download</a>' : 'Kein Dokument'
    },
])
export const tariffHeader = (qualifications: any[], tariffs: any[]) => ([
    {
        label: 'Von',
        sortable: true,
        movable: true,
        class: 'w-28',
        property: 'startDate',
        render: (row: ContractHistory) => colored(row, dayjs(asValue(row.startDate)).format('DD.MM.YYYY'))
    },
    {
        label: 'Bis',
        sortable: true,
        movable: true,
        property: 'endDate',
        render: (row: ContractHistory) => colored(row, whenTerminatedEnd(row))
    },
    {
        label: 'Tarif',
        sortable: true,
        movable: true,
        property: 'tariffId',
        render: (row: ContractHistory) => colored(row, tariffs?.find(x => x.id === row.tariffId)?.name || 'unbekannter Tarif: ' + row.tariffId)
    },
    {
        label: 'Qualifikation',
        sortable: true,
        movable: true,
        property: 'qualificationId',
        render: (row: ContractHistory) => colored(row, qualifications?.find(x => x.id === row.qualificationId)?.title || 'unbekannte Qualifikation: ' + row.qualificationId)
    },
    {
        label: 'Stundensatz',
        sortable: true,
        movable: true,
        property: 'wagePerHour',
        render: (row: ContractHistory) => colored(row, formatCurrency(row.wagePerHour, 'EUR'))
    },
    {
        label: 'Akonto',
        sortable: true,
        movable: true,
        property: 'paymentOnAccount',
        render: (row: ContractHistory) => colored(row, formatCurrency(row.paymentOnAccount, 'EUR'))
    },
    {
        label: 'Nachtzuschlag',
        sortable: false,
        movable: true,
        property: 'addedPercentageNight',
        render: (row: ContractHistory) => colored(row, tariffs?.find(x => x.id === row.tariffId)?.addedPercentageNight + '%')
    },
    {
        label: 'Sonntagszuschlag',
        sortable: false,
        movable: true,
        property: 'addedPercentageNight',
        render: (row: ContractHistory) => colored(row, tariffs?.find(x => x.id === row.tariffId)?.addedPercentageSunday + '%')
    },
    {
        label: 'Feiertagszuschlag',
        sortable: false,
        movable: true,
        property: 'addedPercentageNight',
        render: (row: ContractHistory) => colored(row, tariffs?.find(x => x.id === row.tariffId)?.addedPercentageHoliday + '%')
    },
])

const grayOut = (grayed: boolean, text: string) => grayed ? `<span class="text-neutral-400">${text}</s>` : text;

const whenTerminatedEnd = (row: ContractHistory) => {
    return row.terminated ? dayjs(asValue(row.terminated)).format('DD.MM.YYYY') + ' (<span class="line-through">' + renderDate(row.endDate) + '</span>)' : renderDate(row.endDate);
}

const renderDate = (potentialDate) => {
    if(asValue(potentialDate)) {
        return dayjs(asValue(potentialDate)).format('DD.MM.YYYY')
    }
    return 'unbefristet'
}
const colored = (row: ContractHistory, text: string) => {
    let color = '';
    const now = (new Date()).getTime()
    if (row.employmentContractStatus !== 'synchronized') {
        color = 'text-primary-600';
    } else if(row.startDate.stamp * 1000 > now ) {
        color = 'text-secondary-700';
    } else if(row.endDate.stamp * 1000 < now || row.terminated && row.terminated.stamp * 1000 < now) {
        color = 'text-neutral-400';
    }
    /*if(row.hiredAs.includes('(aufgelöst)') || row.terminated) {
        color += ' line-through';
    }*/
    return `<span class="${color}">${text}</span>`
}